import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPricing } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Check, Truck, ArrowLeft, Loader2 } from 'lucide-react';

const PricingPage = () => {
  const { t } = useTranslation();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const data = await getPricing();
      setPricing(data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl font-['Chivo'] tracking-tight text-slate-900">{t('common.appName')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-orange-100 text-orange-700">{t('nav.pricing')}</Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 font-['Chivo'] tracking-tight mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('pricing.subtitle')} {t('pricing.commission')} {pricing?.commission_enterprise}% {t('pricing.enterprise')} 
            , {pricing?.commission_carrier}% {t('pricing.carrier')}.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {pricing?.plans?.map((plan, index) => (
            <Card 
              key={plan.name}
              className={`relative ${index === 2 ? 'border-orange-500 border-2 shadow-lg' : ''}`}
            >
              {index === 2 && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-orange-500 text-white">{t('pricing.popular')}</Badge>
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl font-['Chivo']">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {plan.price === 0 ? t('pricing.free') : `${plan.price}€`}
                  </span>
                  {plan.price > 0 && <span className="text-slate-500">{t('pricing.perMonth')}</span>}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button 
                    className={`w-full ${index === 2 ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    variant={index === 2 ? 'default' : 'outline'}
                  >
                    {plan.price === 0 ? t('pricing.startFree') : t('pricing.choosePlan')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Commission Info */}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold font-['Chivo'] mb-4">{t('pricing.commission')}</h3>
                <p className="text-slate-300 mb-4">
                  En plus de l&apos;abonnement, une commission est prélevée sur chaque transaction réussie :
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span>{t('pricing.enterprise')} (donneurs d&apos;ordre)</span>
                    <span className="font-['JetBrains_Mono'] font-bold text-orange-400">
                      {pricing?.commission_enterprise}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span>{t('pricing.carrier')}</span>
                    <span className="font-['JetBrains_Mono'] font-bold text-orange-400">
                      {pricing?.commission_carrier}%
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold font-['Chivo'] mb-4">Exemple de calcul</h3>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-300 mb-3">Pour une mission à 1000€ :</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Prix de base</span>
                      <span className="font-['JetBrains_Mono']">1000€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">+ Commission entreprise (6%)</span>
                      <span className="font-['JetBrains_Mono']">60€</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-700 pt-2">
                      <span className="font-medium">Total facturé à l&apos;entreprise</span>
                      <span className="font-['JetBrains_Mono'] font-bold text-emerald-400">1060€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">- Commission transporteur (2.5%)</span>
                      <span className="font-['JetBrains_Mono']">25€</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-700 pt-2">
                      <span className="font-medium">Net versé au transporteur</span>
                      <span className="font-['JetBrains_Mono'] font-bold text-emerald-400">975€</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PricingPage;
